import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// MUI
import { Box, Button, Divider, Typography } from '@mui/material';

// Icons
import AddIcon from '@mui/icons-material/Add';

// Custom
import { BreadCrumbsAndTitle } from '../../../components/generics/BreadCrumbsAndTitle';
import { mtDividerUpTable } from '../../../commons';
import { storeCreateSurvey, storeUpdateSurvey } from '../../../store/ratings/thunks';

// Formik Models
import { formInitialValues } from '../../../components/formik/calificaciones/encuestas/formModels/formInitialValues';

// Hooks
import { useResponseHandler } from '../../../hooks/useResponseHandler';
import { hasPemissions } from '../../../commons/utils';

// Custom Components
// import { TableRatingsSurveys } from './components/TableRatingsSurveys';
import { FormSurvey } from './components/FormSurvey';
import { TableRatingsSurveys } from './components/TableRatingsSurveys';
import { AlertsInformativos } from '../../../components/generics/AlertsInformativos';
import { alertInformativo } from '../../../commons/texts';
import { BannerInfoEncuestas } from './components/BannerInfoEncuestas';

export const ConfiguracionEncuestas = () => {
  const dispatch = useDispatch();
  const { areas_list_select, dinamic_texts_list } = useSelector(
    (state: any) => state.ratingsDevices
  );
  const { handleResponse, ResponseComponents } = useResponseHandler();

  const [view, setView] = useState<'list' | 'form'>('list');
  const [editMode, setEditMode] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState<any>(null);

  const [canCreate, setCanCreate] = useState<boolean>(false);

  React.useEffect(() => {
    checkPermissions();
  }, []);

  const checkPermissions = async () => {
    // const viewPerm = await hasPemissions('modulo.calificaciones', 'modulo.calificacion.encuestas', 'detail');
    const createPerm = await hasPemissions(
      'modulo.calificaciones',
      'modulo.calificacion.configurar.encuestas',
      'create'
    );
    if (createPerm) setCanCreate(true);
  };

  const handleOpenForm = (survey: any = null) => {
    if (survey) {
      const areaId = survey.areasRating?.id || survey.areas_rating_id || null;
      const selectedArea = areas_list_select?.find((a: any) => a.value === areaId) || null;
      const typeId = survey.type || null;
      const selectedType =
        dinamic_texts_list?.find((t: any) => t.value === typeId || t.id === typeId) || null;
      const normalizedSurvey = {
        ...survey,
        type: selectedType
          ? {
              label: selectedType.label || selectedType.name,
              value: selectedType.value || selectedType.id,
            }
          : null,
        areas_rating_id: selectedArea,
        questions: survey.questions.map((q: any) => ({
          ...q,
          isOpenQuestion: q.isOpenQuestion ?? false,
          openQuestionType: q.openQuestionType || 'text',
          imgName: q.imgName && typeof q.imgName === 'object' ? q.imgName.imgName : q.imgName,
          localPreview: '',
          metricType: q.metricType || null,
        })),
        reviewLinks: survey.reviewLinks
          ? survey.reviewLinks.map((link: any) => ({
              value: link.id || link.value,
              label: link.name
                ? `${link.name} (${link.platform || link.label})`
                : link.platform || link.label || link.name,
              icon: link.icon,
            }))
          : [],
      };
      setSelectedSurvey(normalizedSurvey);
      setEditMode(true);
      setIsViewMode(false);
    } else {
      setSelectedSurvey(formInitialValues);
      setEditMode(false);
      setIsViewMode(false);
    }
    setView('form');
  };

  const handleViewForm = (survey: any) => {
    if (survey) {
      const areaId = survey.areasRating?.id || survey.areas_rating_id || null;
      const selectedArea = areas_list_select?.find((a: any) => a.value === areaId) || null;
      const typeId = survey.type || null;
      const selectedType =
        dinamic_texts_list?.find((t: any) => t.value === typeId || t.id === typeId) || null;
      const normalizedSurvey = {
        ...survey,
        type: selectedType
          ? {
              label: selectedType.label || selectedType.name,
              value: selectedType.value || selectedType.id,
            }
          : null,
        areas_rating_id: selectedArea,
        questions: survey.questions.map((q: any) => ({
          ...q,
          isOpenQuestion: q.isOpenQuestion ?? false,
          openQuestionType: q.openQuestionType || 'text',
          imgName: q.imgName && typeof q.imgName === 'object' ? q.imgName.imgName : q.imgName,
          localPreview: '',
          metricType: q.metricType || null,
        })),
        reviewLinks: survey.reviewLinks
          ? survey.reviewLinks.map((link: any) => ({
              value: link.id || link.value,
              label: link.name
                ? `${link.name} (${link.platform || link.label})`
                : link.platform || link.label || link.name,
              icon: link.icon,
            }))
          : [],
      };
      setSelectedSurvey(normalizedSurvey);
      setEditMode(false);
      setIsViewMode(true);
      setView('form');
    }
  };

  const handleBack = () => {
    setView('list');
    setEditMode(false);
    setIsViewMode(false);
    setSelectedSurvey(null);
  };

  const handleSubmit = async (values: any, { resetForm }: any) => {
    let response;
    const payload = {
      ...values,
      type: values.type?.value || null,
      areas_rating_id: values.areas_rating_id?.value || null,
      reviewLinks: values.reviewLinks?.map((link: any) => link.value || link.id) || [],
    };

    if (editMode) {
      response = await dispatch(storeUpdateSurvey({ ...payload, id: selectedSurvey.id }));
    } else {
      response = await dispatch(storeCreateSurvey(payload));
    }

    const actionText = editMode ? 'actualizando encuesta' : 'creando encuesta';
    const ok = await handleResponse(response, actionText);

    if (response.ok) {
      const updatedValues = {
        ...values,
        questions: values.questions.map((q: any) => ({ ...q, localPreview: '' })),
      };

      if (editMode) {
        setSelectedSurvey(updatedValues);
        resetForm({ values: updatedValues });
      } else {
        resetForm();
        setView('list');
      }
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, margin: '0 auto' }}>
      {ResponseComponents}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <BreadCrumbsAndTitle
            title={
              view === 'list'
                ? 'Configuración de Encuestas'
                : editMode
                  ? 'Editar Encuesta'
                  : 'Crear Encuesta'
            }
            mainModule="calificaciones"
            parent="Configuraciones"
            icon="SettingsSuggest"
            action="Encuestas"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <AlertsInformativos
              type={'info'}
              title={'info'}
              content={alertInformativo.configuracionEncuestas}
            />
          </Typography>
        </Box>

        {view === 'list' && canCreate && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenForm()}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0px 4px 8px -4px rgba(105, 108, 255, 0.42)',
            }}
          >
            Nueva Encuesta
          </Button>
        )}
      </Box>

      <Divider sx={{ marginBottom: mtDividerUpTable }} />

      {view === 'list' ? (
        <>
          <TableRatingsSurveys onEdit={handleOpenForm} onView={handleViewForm} />
          <BannerInfoEncuestas />
        </>
      ) : (
        <FormSurvey
          selectedSurvey={selectedSurvey}
          editMode={editMode}
          isViewMode={isViewMode}
          handleSubmit={handleSubmit}
          handleBack={handleBack}
          handleResponse={handleResponse}
        />
      )}
    </Box>
  );
};
